// Eigenbau: Pipeline-Kennzahlen, Job-Zeitleiste und Log-Zugriff; Tremor
// liefert nur Tracker und Badge, keine Job-Timeline ohne neues Paket. Die
// Zeitleiste positioniert jeden Job relativ zum Gesamtzeitraum der Pipeline —
// Wartezeit und Laufzeit werden so direkt sichtbar. Karten sind aufklappbar;
// fehlgeschlagene Jobs werden unter der Zeitleiste aufgelistet.
import { useState } from "react";
import { useSekundentakt } from "@/lib/sekundentakt";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { schluessel } from "@/lib/abfragen";
import { Badge, type BadgeProps } from "@/components/Badge";
import { Zustandsmarke } from "@/components/entwurf/Zustandsmarke";
import { Button } from "@/components/Button";
import { Card } from "@/components/Card";
import { Tracker, type TrackerBlock } from "@/components/Tracker";
import { Tooltip } from "@/components/Tooltip";
import { LogAnsicht } from "./LogAnsicht";
import { ChevronRight } from "lucide-react";

export type PipelineAnzeige = {
  id: string;
  gitlabId: number;
  zustand: string;
  dauerSekunden: number | null;
  commitSha: string | null;
  gitlabRef: string | null;
  istTag: boolean | null;
  quelle: string | null;
  ausloeserName: string | null;
  ausloeserUsername: string | null;
  /** ISO-Zeitpunkte (tRPC serialisiert Datumswerte als Strings). */
  gitlabErstelltAm: string | null;
  gitlabGestartetAm: string | null;
  gitlabFertigAm: string | null;
  queuedDauerSekunden: number | null;
  stages: string[] | null;
  builds: Record<string, unknown>[] | null;
  commitTitel: string | null;
};

export type PipelineJobAnzeige = {
  id: number | null;
  stage: string;
  name: string;
  status: string | null;
  dauerSekunden: number | null;
  queuedDauerSekunden: number | null;
  fehlergrund: string | null;
  manuell: boolean | null;
  erlaubtFehler: boolean | null;
  erstelltAm: string | null;
  gestartetAm: string | null;
  fertigAm: string | null;
};

const REVIEW_ABGLEICH_JOB = "Review-Umgebungen abgleichen";

/**
 * Ein automatischer Review-Abgleich, den die Übersicht ausblenden darf.
 *
 * GitLabs Pipeline-Nutzlast belegt nur, welche Jobs liefen und wie sie
 * endeten. Ob der Abgleich intern tatsächlich etwas auf- oder abgebaut hat,
 * steht ausschließlich im Job-Log. Deshalb wird hier bewusst nicht
 * „No-op" behauptet und auch nichts gelöscht: Nur erfolgreiche, automatische
 * Ein-Job-Pipelines wandern hinter den Schalter. Fehler, laufende Abgleiche
 * und gemischte Pipelines bleiben immer sichtbar.
 */
export function istErfolgreicherReviewAbgleich(
  pipeline: PipelineAnzeige,
): boolean {
  const quelle = pipeline.quelle?.trim().toLowerCase();
  if (
    pipeline.zustand.trim().toLowerCase() !== "success" ||
    (quelle !== "schedule" && quelle !== "trigger")
  ) {
    return false;
  }

  const builds = pipeline.builds ?? [];
  if (builds.length !== 1) return false;
  const [job] = builds;
  return (
    job?.["name"] === REVIEW_ABGLEICH_JOB &&
    typeof job["status"] === "string" &&
    job["status"].trim().toLowerCase() === "success"
  );
}

/**
 * Trennt die ausblendbaren Abgleiche vom Rest. Beide Listen behalten die
 * Reihenfolge, in der sie hereinkamen — eingeblendet steht damit wieder
 * jeder Lauf an seiner Stelle im Zeitverlauf.
 */
export function teileReviewAbgleiche(pipelines: PipelineAnzeige[]): {
  uebrige: PipelineAnzeige[];
  abgleiche: PipelineAnzeige[];
} {
  const uebrige: PipelineAnzeige[] = [];
  const abgleiche: PipelineAnzeige[] = [];
  for (const pipeline of pipelines) {
    if (istErfolgreicherReviewAbgleich(pipeline)) abgleiche.push(pipeline);
    else uebrige.push(pipeline);
  }
  return { uebrige, abgleiche };
}

export function zustandAnzeige(
  zustandRoh: string,
  /*
   * Gesetzt, wenn die Zeile ihren Zustand nicht mehr wissen kann. Sie bekommt
   * dann eine eigene Beschriftung statt „Läuft" — der Unterschied zwischen
   * „läuft gerade" und „wir haben seit Stunden nichts gehört" ist genau die
   * Auskunft, die hier fehlte.
   */
  veraltet = false,
): {
  label: string;
  badge: BadgeProps["variant"];
  trackerFarbe: TrackerBlock["farbe"];
} {
  const zustand = zustandRoh.trim().toLowerCase();
  if (veraltet) {
    return {
      label: "Zustand veraltet",
      badge: "warning",
      trackerFarbe: "gelb",
    };
  }
  if (zustand === "success")
    return { label: "Erfolgreich", badge: "success", trackerFarbe: "gruen" };
  if (zustand === "failed")
    return { label: "Fehlgeschlagen", badge: "error", trackerFarbe: "rot" };
  if (zustand === "running")
    return { label: "Läuft", badge: "default", trackerFarbe: "blau" };
  if (
    ["created", "pending", "preparing", "waiting_for_resource", "scheduled"].includes(
      zustand,
    )
  )
    return { label: "Wartet", badge: "warning", trackerFarbe: "gelb" };
  if (zustand === "canceled" || zustand === "cancelled")
    return { label: "Abgebrochen", badge: "neutral", trackerFarbe: "grau" };
  if (zustand === "skipped")
    return { label: "Übersprungen", badge: "neutral", trackerFarbe: "grau" };
  if (zustand === "manual")
    return { label: "Manuell", badge: "neutral", trackerFarbe: "grau" };
  return {
    label: zustandRoh || "Unbekannt",
    badge: "neutral",
    trackerFarbe: "grau",
  };
}

/**
 * Was in der Zeitspalte eines Laufs steht.
 *
 * „keine Dauer" war für einen **laufenden** Lauf keine Auskunft: Eine
 * Enddauer hat er noch nicht, eine Laufzeit sehr wohl. Solange er läuft, wird
 * sie deshalb aus dem Startzeitpunkt gerechnet und als „seit …" gezeigt.
 *
 * Fehlt auch der Startzeitpunkt, steht dort „gestartet" — das ist alles, was
 * dann bekannt ist, und immer noch mehr als eine Verneinung.
 */
export function laufzeitText(
  pipeline: PipelineAnzeige,
  /* Von außen gereicht, damit die Anzeige mitzählt — siehe `useSekundentakt`. */
  jetzt: number = Date.now(),
): string {
  if (pipeline.dauerSekunden !== null) {
    return formatiereDauer(pipeline.dauerSekunden);
  }
  if (!istLaufend(pipeline)) return "—";

  const start = pipeline.gitlabGestartetAm ?? pipeline.gitlabErstelltAm;
  if (!start) return "gestartet";
  /*
   * Jenseits der Frist wird nicht weitergezählt. Eine mitlaufende Uhr an einer
   * Zeile, die seit zwei Tagen keine Rückmeldung bekommen hat, ist keine
   * Auskunft über den Lauf, sondern über unsere eigene Ahnungslosigkeit — und
   * sie sieht aus wie das Gegenteil.
   */
  if (istZustandVeraltet(pipeline, jetzt)) {
    return "ohne Rückmeldung";
  }
  const sekunden = Math.max(
    0,
    Math.round((jetzt - new Date(start).getTime()) / 1_000),
  );
  return `seit ${formatiereDauer(sekunden)}`;
}

/** Läuft dieser Lauf noch — oder wartet er darauf, loszulaufen? */
export function istLaufend(pipeline: PipelineAnzeige): boolean {
  const zustand = pipeline.zustand.trim().toLowerCase();
  return (
    zustand === "running" ||
    ["created", "pending", "preparing", "waiting_for_resource", "scheduled"].includes(
      zustand,
    )
  );
}

/**
 * Ab wann „Läuft" keine Auskunft mehr ist, sondern eine stehengebliebene Zeile.
 *
 * **Gelernt am 14.08.2026:** Pipeline 949 stand 46 Stunden auf „Läuft · seit
 * 2769 min". In GitLab war sie seit dem 12.08. um 17:26 erfolgreich, 3 min 43 s
 * — der Webhook, der den Endzustand gebracht hätte, wurde mit 401 abgewiesen,
 * und der Nachzug holt nur die letzten fünfzig Pipelines. Die Anzeige hat den
 * alten Stand nicht bloß behalten, sie hat ihn sekundenweise weitergezählt und
 * damit fortlaufend behauptet, gerade jetzt liefe dort etwas.
 *
 * Sechs Stunden, nicht sechzig Minuten: Die Läufe dieses Hauses brauchen drei
 * bis fünf Minuten, aber eine Kette aus mehreren Stufen darf länger dauern,
 * ohne dass die Anzeige sie für tot erklärt. Jenseits davon ist ein verlorener
 * Hook die bei Weitem wahrscheinlichere Erklärung als ein lebender Lauf.
 *
 * Dieselbe Regel wie bei den Auto-Q&A-Aufträgen (Paket 19, G6) und bei den
 * Durchsicht-Läufen: Was keinen Fortschritt mehr meldet, gilt nicht weiter als
 * laufend.
 */
export const ZUSTAND_UNGLAUBWUERDIG_MS = 6 * 60 * 60 * 1_000;

/**
 * Behauptet die Zeile etwas, das sie nicht mehr wissen kann?
 *
 * Nur für laufende und wartende Zustände: Ein `success` von gestern ist kein
 * veralteter Zustand, sondern ein abgeschlossener.
 */
export function istZustandVeraltet(
  pipeline: PipelineAnzeige,
  jetzt: number = Date.now(),
): boolean {
  if (!istLaufend(pipeline)) return false;
  const start = pipeline.gitlabGestartetAm ?? pipeline.gitlabErstelltAm;
  if (!start) return false;
  return jetzt - new Date(start).getTime() > ZUSTAND_UNGLAUBWUERDIG_MS;
}

export function pipelineTrackerBlock(
  pipeline: PipelineAnzeige,
  jetzt?: number,
): TrackerBlock {
  const anzeige = zustandAnzeige(
    pipeline.zustand,
    istZustandVeraltet(pipeline, jetzt),
  );
  const laufzeit = laufzeitText(pipeline, jetzt);
  return {
    key: pipeline.id,
    farbe: anzeige.trackerFarbe,
    status: anzeige.label,
    /*
     * Im Block steht, was ihn identifiziert und wie lange er brauchte. Der
     * Zustand fehlt dort mit Absicht — den sagt schon die Farbe, und ein Wort
     * mehr würde in schmalen Balken nur beide verdrängen. Vollständig bleibt
     * der Tooltip.
     */
    titel: `#${pipeline.gitlabId}`,
    unterzeile: laufzeit,
    tooltip: `Pipeline ${pipeline.gitlabId} · ${anzeige.label} · ${laufzeit} · ${
      pipeline.commitSha?.slice(0, 7) || "keine SHA"
    }`,
  };
}

export function formatiereDauer(sekunden: number | null): string {
  if (sekunden === null) return "—";
  if (sekunden < 60) return `${sekunden} s`;
  const minuten = Math.floor(sekunden / 60);
  const rest = sekunden % 60;
  return rest === 0 ? `${minuten} min` : `${minuten} min ${rest} s`;
}

function formatiereZeitpunkt(wert: string | null): string {
  if (!wert) return "—";
  return new Date(wert).toLocaleString("de-DE", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function quelleAnzeige(quelle: string | null): string {
  const tabelle: Record<string, string> = {
    push: "Push",
    merge_request_event: "Merge Request",
    schedule: "Zeitplan",
    api: "API",
    web: "Web",
    trigger: "Auslöser",
    parent_pipeline: "Übergeordnete Pipeline",
    external: "Extern",
    chat: "Chat",
    webide: "WebIDE",
    pipeline: "Pipeline",
  };
  if (!quelle) return "—";
  return tabelle[quelle] ?? quelle;
}

function jobAusBuild(build: Record<string, unknown>): PipelineJobAnzeige {
  const nummer = (wert: unknown): number | null =>
    typeof wert === "number" && Number.isFinite(wert) ? wert : null;
  const text = (wert: unknown): string | null =>
    typeof wert === "string" && wert !== "" ? wert : null;
  const bool = (wert: unknown): boolean | null =>
    typeof wert === "boolean" ? wert : null;
  return {
    id: nummer(build["id"]),
    stage: text(build["stage"]) ?? "",
    name: text(build["name"]) ?? "Unbenannter Job",
    status: text(build["status"]),
    dauerSekunden: nummer(build["duration"]),
    queuedDauerSekunden: nummer(build["queued_duration"]),
    fehlergrund: text(build["failure_reason"]),
    manuell: bool(build["manual"]),
    erlaubtFehler: bool(build["allow_failure"]),
    erstelltAm: text(build["created_at"]),
    gestartetAm: text(build["started_at"]),
    fertigAm: text(build["finished_at"]),
  };
}

/** Tailwind-Hintergrund für den Job-Balken je Status. */
function jobBalkenFarbe(status: string | null): string {
  const zustand = (status ?? "").trim().toLowerCase();
  if (zustand === "success") return "bg-emerald-500";
  if (zustand === "failed") return "bg-red-500 dark:bg-red-600";
  if (zustand === "running") return "animate-pulse bg-blue-500";
  if (
    ["created", "pending", "preparing", "waiting_for_resource", "scheduled"].includes(
      zustand,
    )
  )
    return "bg-amber-500";
  if (zustand === "manual") return "bg-sky-400";
  return "bg-gray-500";
}

/** Farbe für den Stage-Badge (kleine kontextuelle Markierung). */
function stageFarbe(index: number): string {
  const farben = [
    "bg-blue-100 text-blue-700 dark:bg-blue-950 dark:text-blue-300",
    "bg-purple-100 text-purple-700 dark:bg-purple-950 dark:text-purple-300",
    "bg-amber-100 text-amber-700 dark:bg-amber-950 dark:text-amber-300",
    "bg-emerald-100 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300",
    "bg-rose-100 text-rose-700 dark:bg-rose-950 dark:text-rose-300",
  ];
  return farben[index % farben.length]!;
}

function zeitpunkt(wert: string | null): number | null {
  if (!wert) return null;
  const ms = Date.parse(wert);
  return Number.isNaN(ms) ? null : ms;
}

type Zeitraum = { start: number; ende: number };

function baueZeitraum(
  jobs: PipelineJobAnzeige[],
  pipeline: PipelineAnzeige,
): Zeitraum | null {
  const punkte: number[] = [];
  for (const job of jobs) {
    const start = zeitpunkt(job.gestartetAm) ?? zeitpunkt(job.erstelltAm);
    const ende = zeitpunkt(job.fertigAm);
    if (start !== null) punkte.push(start);
    if (ende !== null) punkte.push(ende);
    if (start !== null && ende === null && job.dauerSekunden !== null) {
      punkte.push(start + job.dauerSekunden * 1_000);
    }
  }
  if (punkte.length === 0) {
    const start = zeitpunkt(pipeline.gitlabErstelltAm);
    const ende =
      zeitpunkt(pipeline.gitlabFertigAm) ??
      (start !== null && pipeline.dauerSekunden !== null
        ? start + pipeline.dauerSekunden * 1_000
        : null);
    if (start !== null && ende !== null && ende > start) return { start, ende };
    return null;
  }
  let start = Math.min(...punkte);
  let ende = Math.max(...punkte);
  if (ende <= start) {
    if (pipeline.dauerSekunden !== null) ende = start + pipeline.dauerSekunden * 1_000;
    else return null;
  }
  return { start, ende };
}

function jobBalkenStil(
  job: PipelineJobAnzeige,
  zeitraum: Zeitraum | null,
  index: number,
  anzahl: number,
): { links: number; breite: number } {
  if (!zeitraum) {
    const breite = 100 / anzahl;
    return { links: index * breite, breite: Math.max(breite - 1, 2) };
  }
  const spanne = zeitraum.ende - zeitraum.start;
  const start =
    zeitpunkt(job.gestartetAm) ??
    zeitpunkt(job.erstelltAm) ??
    zeitraum.start;
  const ende =
    zeitpunkt(job.fertigAm) ??
    (start !== null && job.dauerSekunden !== null
      ? start + job.dauerSekunden * 1_000
      : zeitraum.ende);
  const links = Math.max(((start - zeitraum.start) / spanne) * 100, 0);
  const breite = Math.max(((ende - start) / spanne) * 100, 3);
  return { links, breite };
}

/** Einzelne Stufe (Stage) — gruppiert die Jobs je Name. */
function stageNameAusJobs(jobs: PipelineJobAnzeige[]): string[] {
  return [...new Set(jobs.map((j) => j.stage).filter(Boolean))];
}

/** Höhe einer Spur und der Abstand dazwischen, in Pixeln. */
const SPUR_HOEHE = 22;
const SPUR_ABSTAND = 4;

/**
 * Verteilt die Balken auf Spuren, sodass sich keine zwei überlappen.
 *
 * Gleichzeitig laufende Jobs belegen denselben Zeitbereich. Auf einer einzigen
 * Zeile lagen sie deshalb übereinander und übermalten sich — von vier parallelen
 * Jobs war genau einer zu sehen, die anderen drei verschwanden unter ihm. Jeder
 * Balken bekommt jetzt die erste Spur, die an seiner Startstelle frei ist. Die
 * Leiste wächst nach unten, statt zu überdecken: Nebenläufigkeit wird damit
 * ablesbar, statt Schaden anzurichten.
 */
export function verteileAufSpuren(
  balken: readonly { links: number; breite: number }[],
): { spuren: number[]; anzahl: number } {
  const spurEnde: number[] = [];
  const spuren = new Array<number>(balken.length).fill(0);
  const reihenfolge = balken
    .map((balk, index) => ({
      index,
      start: balk.links,
      ende: balk.links + balk.breite,
    }))
    .sort((a, b) => a.start - b.start || a.ende - b.ende);

  for (const { index, start, ende } of reihenfolge) {
    // Etwas Luft, damit zwei Balken derselben Spur nicht aneinanderkleben.
    let spur = spurEnde.findIndex((frei) => frei <= start + 0.05);
    if (spur === -1) {
      spurEnde.push(ende);
      spur = spurEnde.length - 1;
    } else {
      spurEnde[spur] = ende;
    }
    spuren[index] = spur;
  }

  return { spuren, anzahl: Math.max(spurEnde.length, 1) };
}

function JobZeitleiste({
  pipeline,
  jobs,
  offenesLog,
  setzeOffenesLog,
}: {
  pipeline: PipelineAnzeige;
  jobs: PipelineJobAnzeige[];
  offenesLog: { pipelineId: string; jobId: number } | null;
  setzeOffenesLog: (log: { pipelineId: string; jobId: number } | null) => void;
}) {
  const zeitraum = baueZeitraum(jobs, pipeline);
  const mitZeiten = zeitraum !== null;
  const stile = jobs.map((job, index) =>
    jobBalkenStil(job, zeitraum, index, jobs.length),
  );
  const { spuren, anzahl: spurAnzahl } = verteileAufSpuren(stile);
  const leistenHoehe =
    spurAnzahl * SPUR_HOEHE + (spurAnzahl + 1) * SPUR_ABSTAND;

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-0.5 text-[11px] text-gray-500 dark:text-gray-400">
        <span className="font-medium text-gray-600 dark:text-gray-300">
          {jobs.length} Jobs
          {spurAnzahl > 1 ? (
            <span className="font-normal text-gray-500 dark:text-gray-400">
              {" "}
              · bis zu {spurAnzahl} gleichzeitig
            </span>
          ) : null}
        </span>
        {mitZeiten ? (
          <span className="tabular-nums">
            {formatiereZeitpunkt(
              new Date(zeitraum.start).toISOString(),
            )}{" "}
            – {formatiereZeitpunkt(new Date(zeitraum.ende).toISOString())}
          </span>
        ) : (
          <span>Ohne Zeitangaben gleichmäßig verteilt</span>
        )}
      </div>
      <div
        className="relative w-full overflow-hidden rounded-lg bg-gray-100 dark:bg-gray-900"
        style={{ height: `${leistenHoehe}px` }}
        role="group"
        aria-label={`Zeitleiste der ${jobs.length} Jobs von Pipeline ${pipeline.gitlabId}`}
      >
        {jobs.map((job, index) => {
          const anzeige = zustandAnzeige(job.status ?? "");
          const stil = stile[index]!;
          const spur = spuren[index]!;
          const logOffen =
            offenesLog?.pipelineId === pipeline.id &&
            offenesLog?.jobId === job.id;
          return (
            <Tooltip
              key={`${job.id ?? job.name}-${index}`}
              side="top"
              sideOffset={4}
              showArrow={false}
              className="z-[999]"
              // Ohne dies rendert der Radix-Trigger einen eigenen <button> um
              // unseren — verschachtelte Buttons sind ungültiges HTML, und React
              // meldet es als Hydrationsfehler in der Konsole.
              triggerAsChild
              content={
                <div className="max-w-72 space-y-0.5">
                  <p className="font-semibold">
                    {job.name}{" "}
                    <span className="font-normal text-gray-300">
                      · {job.stage || "ohne Stage"}
                    </span>
                  </p>
                  <p>
                    {anzeige.label}
                    {job.dauerSekunden !== null
                      ? ` · ${formatiereDauer(job.dauerSekunden)}`
                      : ""}
                    {job.queuedDauerSekunden !== null
                      ? ` · wartete ${formatiereDauer(job.queuedDauerSekunden)}`
                      : ""}
                  </p>
                  {job.fehlergrund ? (
                    <p className="text-red-300">{job.fehlergrund}</p>
                  ) : null}
                  {job.manuell || job.erlaubtFehler ? (
                    <p className="text-gray-300">
                      {job.manuell ? "manuell" : ""}
                      {job.manuell && job.erlaubtFehler ? " · " : ""}
                      {job.erlaubtFehler ? "Fehler erlaubt" : ""}
                    </p>
                  ) : null}
                  {job.id !== null ? (
                    <p className="text-gray-300">
                      {logOffen
                        ? "Log geöffnet — erneut klicken zum Schließen"
                        : "Klicken für Log"}
                    </p>
                  ) : null}
                </div>
              }
            >
              <button
                type="button"
                aria-label={`${job.name} (${anzeige.label})${job.id !== null ? " — Log öffnen" : ""}`}
                className={`absolute flex items-center overflow-hidden rounded transition-[filter,box-shadow] duration-150 ${jobBalkenFarbe(job.status)} ${logOffen ? "ring-2 ring-blue-500 ring-offset-1 ring-offset-gray-50 dark:ring-offset-gray-950" : ""} ${job.id !== null ? "cursor-pointer hover:brightness-110 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-500" : "cursor-default"}`}
                style={{
                  left: `${stil.links}%`,
                  // Zwei Pixel Luft, damit aufeinanderfolgende Balken derselben
                  // Spur als zwei Jobs lesbar bleiben und nicht als einer.
                  width: `max(calc(${stil.breite}% - 2px), 3px)`,
                  top: `${SPUR_ABSTAND + spur * (SPUR_HOEHE + SPUR_ABSTAND)}px`,
                  height: `${SPUR_HOEHE}px`,
                }}
                disabled={job.id === null}
                onClick={() => {
                  if (job.id === null) return;
                  setzeOffenesLog(
                    logOffen
                      ? null
                      : { pipelineId: pipeline.id, jobId: job.id },
                  );
                }}
              >
                {stil.breite > 12 ? (
                  <span className="truncate px-1.5 text-[10px] font-semibold leading-none text-white">
                    {job.name}
                  </span>
                ) : null}
              </button>
            </Tooltip>
          );
        })}
      </div>
    </div>
  );
}

/** Zeigt fehlgeschlagene Jobs mit Fehlergrund direkt an (kein Tooltip nötig). */
function FehlgeschlageneJobsListe({ jobs }: { jobs: PipelineJobAnzeige[] }) {
  const fehlgeschlagen = jobs.filter(
    (j) => j.status?.trim().toLowerCase() === "failed",
  );
  if (fehlgeschlagen.length === 0) return null;
  return (
    <div className="space-y-1.5 rounded-lg bg-red-50 px-3 py-2.5 text-xs dark:bg-red-950/40">
      <p className="font-semibold text-red-700 dark:text-red-400">
        {/* Vorher stand im Singular „1 fehlgeschlagenen Job" — falscher Fall. */}
        {fehlgeschlagen.length === 1
          ? "1 fehlgeschlagener Job"
          : `${fehlgeschlagen.length} fehlgeschlagene Jobs`}
      </p>
      {fehlgeschlagen.map((job) => (
        <div
          key={job.id ?? job.name}
          className="flex flex-wrap items-baseline gap-x-2"
        >
          <span className="font-mono font-medium text-red-700 dark:text-red-300">
            {job.name}
          </span>
          <span className="text-red-600/80 dark:text-red-400/80">
            {job.stage || "ohne Stage"}
          </span>
          {job.fehlergrund ? (
            <span className="text-red-600/80 dark:text-red-400/80">
              · {job.fehlergrund}
            </span>
          ) : null}
        </div>
      ))}
    </div>
  );
}

function PipelineKarte({
  projektId,
  pipeline,
  offenesLog,
  setzeOffenesLog,
  jetzt,
}: {
  projektId: string;
  pipeline: PipelineAnzeige;
  offenesLog: { pipelineId: string; jobId: number } | null;
  setzeOffenesLog: (log: { pipelineId: string; jobId: number } | null) => void;
  /** Sekundentakt aus dem Abschnitt — lässt die Laufzeit mitzählen. */
  jetzt: number;
}) {
  const veraltet = istZustandVeraltet(pipeline, jetzt);
  const anzeige = zustandAnzeige(pipeline.zustand, veraltet);
  const jobs = (pipeline.builds ?? []).map(jobAusBuild);
  const stageNamen = stageNameAusJobs(jobs);
  // Standardmäßig eingeklappt — erst der Klick auf die Kopfzeile öffnet die
  // Details. So bleiben viele Pipelines auf einen Blick sichtbar.
  const [offen, setzeOffen] = useState(false);

  // Log nur laden, wenn der offene Job zu DIESER Pipeline gehört.
  const logAktiv =
    offenesLog?.pipelineId === pipeline.id && offenesLog !== null;
  const log = useQuery({
    queryKey: schluessel.gitlabJobTrace(projektId, offenesLog?.jobId ?? 0),
    queryFn: () =>
      api.gitlab.jobTrace.query({
        projektId,
        jobId: offenesLog!.jobId,
      }),
    enabled: logAktiv,
  });

  /* Die Job-Zählung ist mit der Kurzfassung dorthin gewandert. */

  /** Ob der Ref ein MR-typischer Pfad ist. */
  const istMrRef =
    pipeline.gitlabRef?.startsWith("refs/merge-requests/") === true;

  const ausloeser = pipeline.ausloeserName
    ? `${pipeline.ausloeserName}${
        pipeline.ausloeserUsername ? ` · @${pipeline.ausloeserUsername}` : ""
      }`
    : null;

  return (
    <Card className="space-y-4 p-4 transition-shadow duration-150 ease-aus hover:shadow-md">
      {/*
        Die Kopfzeile war ein Div, das nur im zugeklappten Zustand ein
        `role="button"` trug, danebst ein zweiter Knopf für den Pfeil. Zwei
        Bedienelemente für eine Handlung, und `aria-expanded` klebte im offenen
        Zustand an einem Div ohne Rolle. Jetzt ist die ganze Zeile ein Schalter.

        Zugleich trägt sie die Rangfolge: Zustand und Branch führen — danach wird
        gesucht. Nummer, Auslöser und Zeitpunkt treten in die zweite Zeile
        zurück, statt gleichgewichtig um Aufmerksamkeit zu konkurrieren.
      */}
      {/*
        Der Schalter umfasst die **ganze zugeklappte Karte**, nicht nur die
        Kopfzeile: Die ganze Fläche klappt auf.

        Deshalb liegen Kopfzeile, Kennzeile und Kurzfassung darin, und die
        Kurzfassung wird hier gezeigt statt weiter unten. Nur der aufgeklappte
        Teil bleibt außerhalb: Er enthält Job-Schalter und Log-Verweise, und
        ein Bedienelement im Bedienelement wäre weder gültig noch bedienbar.

        Innen stehen `span` statt `div`: Ein Schalter darf nur Phraseninhalt
        tragen. Das Aussehen macht `flex`, nicht die Elementart.
      */}
      <button
        type="button"
        onClick={() => setzeOffen(!offen)}
        aria-expanded={offen}
        aria-label={`Pipeline ${pipeline.gitlabId} ${offen ? "zuklappen" : "aufklappen"}`}
        className="flex w-full cursor-pointer flex-col gap-1.5 rounded-md text-left focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-500"
      >
        <span className="flex w-full items-center gap-2.5">
          <ChevronRight
            aria-hidden="true"
            className={`size-4 shrink-0 text-gray-400 transition-transform duration-150 ${offen ? "rotate-90" : ""}`}
          />
          {/*
            Eigene Marke statt `Badge`: Tremors `default` ist blau, und Blau
            ist im Haus der Akzent — „Läuft" war dadurch dasselbe Grün wie
            „Erfolgreich". Die Marke nimmt dieselbe Farbe wie die Leiste.
          */}
          <Zustandsmarke farbe={anzeige.trackerFarbe}>
            {anzeige.label}
          </Zustandsmarke>
          <span className="flex min-w-0 flex-1 items-center gap-1.5">
            <span className="truncate font-mono text-[13px] text-gray-900 dark:text-gray-50">
              {pipeline.gitlabRef || "—"}
            </span>
            {pipeline.istTag ? (
              <Badge variant="neutral">Tag</Badge>
            ) : istMrRef ? (
              <Badge variant="neutral">MR</Badge>
            ) : null}
          </span>
          <span className="shrink-0 text-xs font-semibold tabular-nums text-gray-700 dark:text-gray-300">
            {laufzeitText(pipeline, jetzt)}
          </span>
        </span>

        <span className="flex flex-wrap items-center gap-x-2 gap-y-0.5 pl-[26px] text-xs text-gray-500 dark:text-gray-400">
          <span className="font-mono tabular-nums text-gray-400 dark:text-gray-500">
            #{pipeline.gitlabId}
          </span>
          <span aria-hidden="true">·</span>
          <span>{quelleAnzeige(pipeline.quelle)}</span>
          {ausloeser ? (
            <>
              <span aria-hidden="true">·</span>
              <span className="truncate">{ausloeser}</span>
            </>
          ) : null}
          <span aria-hidden="true">·</span>
          <span className="tabular-nums">
            {formatiereZeitpunkt(pipeline.gitlabErstelltAm)}
          </span>
          {pipeline.queuedDauerSekunden !== null ? (
            <span className="text-gray-400 dark:text-gray-500">
              wartete {formatiereDauer(pipeline.queuedDauerSekunden)}
            </span>
          ) : null}
        </span>

        {/* Die Kurzfassung gehört zur zugeklappten Fläche und damit in den Schalter. */}
        {!offen ? <Kurzfassung pipeline={pipeline} jobs={jobs} /> : null}
      </button>

      {offen ? (
        <>
          {/*
            Stufen, Zeitleiste und Fehlerliste beschreiben denselben Lauf und
            stehen deshalb eng beieinander; der Abstand der Karte trennt sie von
            der Commit-Zeile darunter. Vorher hatte die Karte überhaupt keinen
            Rhythmus — alles lag auf Kante, und ein `-mb-1` hielt dagegen.
          */}
          <div className="space-y-2">
            {stageNamen.length > 0 ? (
              <div className="flex flex-wrap gap-1">
                {stageNamen.map((stage, idx) => (
                  <span
                    key={stage}
                    className={`rounded px-1.5 py-0.5 text-[10px] font-semibold ${stageFarbe(idx)}`}
                  >
                    {stage}{" "}
                    <span className="font-normal opacity-70 tabular-nums">
                      {jobs.filter((j) => j.stage === stage).length}
                    </span>
                  </span>
                ))}
              </div>
            ) : null}

            {jobs.length === 0 ? (
              <p className="rounded-lg bg-gray-100/80 px-3 py-4 text-center text-xs text-gray-500 dark:bg-gray-900 dark:text-gray-400">
                Keine Job-Details gespeichert — der Webhook enthielt keine
                builds.
              </p>
            ) : (
              <>
                <JobZeitleiste
                  pipeline={pipeline}
                  jobs={jobs}
                  offenesLog={offenesLog}
                  setzeOffenesLog={setzeOffenesLog}
                />
                <FehlgeschlageneJobsListe jobs={jobs} />
              </>
            )}
          </div>

          {/*
            Commit-Zeile. Ohne Titel stand hier vorher die volle SHA hinter der
            gekürzten — dieselbe Angabe zweimal, einmal unlesbar lang.
          */}
          <div className="flex flex-wrap items-baseline gap-x-2 text-xs text-gray-500 dark:text-gray-400">
            <span className="font-mono tabular-nums text-gray-400 dark:text-gray-500">
              {pipeline.commitSha?.slice(0, 7) || "—"}
            </span>
            {pipeline.commitTitel ? (
              <span className="min-w-0 truncate">{pipeline.commitTitel}</span>
            ) : null}
          </div>

          {/* Log-Bereich: nur wenn der offene Job zu dieser Pipeline gehört */}
          {offenesLog?.pipelineId === pipeline.id && offenesLog ? (
            <div className="pt-1">
              {log.isLoading ? (
                <p className="text-sm text-gray-500">Lade Log …</p>
              ) : log.isError ? (
                <p className="text-sm text-red-600" role="alert">
                  Log konnte nicht geladen werden. Ist der Lese-Zugang
                  (GITLAB_HOST / GITLAB_TOKEN) eingerichtet?
                </p>
              ) : log.data?.eingerichtet === true ? (
                <LogAnsicht
                  text={log.data.text}
                  abgeschnitten={log.data.abgeschnitten}
                />
              ) : (
                <p className="text-sm text-amber-600 dark:text-amber-400">
                  Logs werden live aus GitLab geladen. Dafür fehlt die
                  Einrichtung von GITLAB_HOST und GITLAB_TOKEN.
                </p>
              )}
            </div>
          ) : null}
        </>
      ) : null}
    </Card>
  );
}

/**
 * Die Kurzfassung eines zugeklappten Laufs: Jobs, Ausgang, Commit.
 *
 * Steht als eigener Baustein da, weil sie **innerhalb** des Schalters liegt,
 * der die ganze zugeklappte Karte trägt. Deshalb `span` statt `div`: Ein
 * Schalter darf nur Phraseninhalt tragen.
 *
 * Die Trennpunkte stehen zwischen den Gliedern, nicht davor — vorher begann
 * die Zeile ohne Jobs mit einem einzelnen „·".
 */
function Kurzfassung({
  pipeline,
  jobs,
}: {
  pipeline: PipelineAnzeige;
  jobs: PipelineJobAnzeige[];
}) {
  const zaehlung = (status: string) =>
    jobs.filter((job) => (job.status ?? "").trim().toLowerCase() === status)
      .length;
  const erfolgreich = zaehlung("success");
  const fehler = zaehlung("failed");
  const andere = jobs.length - erfolgreich - fehler;

  return (
    <span className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5 pl-[26px] text-[11px] text-gray-500 dark:text-gray-400">
      {jobs.length > 0 ? (
        <>
          <span className="tabular-nums">{jobs.length} Jobs</span>
          <span aria-hidden="true">·</span>
          <span className="tabular-nums text-emerald-600 dark:text-emerald-400">
            {erfolgreich} bestanden
          </span>
          {fehler > 0 ? (
            <>
              <span aria-hidden="true">·</span>
              <span className="font-medium tabular-nums text-red-600 dark:text-red-400">
                {fehler} fehlgeschlagen
              </span>
            </>
          ) : null}
          {andere > 0 ? (
            <>
              <span aria-hidden="true">·</span>
              <span className="tabular-nums">{andere} weitere</span>
            </>
          ) : null}
          <span aria-hidden="true">·</span>
        </>
      ) : null}
      <span className="min-w-0 truncate">
        <span className="font-mono text-gray-400 dark:text-gray-500">
          {pipeline.commitSha?.slice(0, 7) || "—"}
        </span>
        {pipeline.commitTitel ? ` ${pipeline.commitTitel}` : ""}
      </span>
    </span>
  );
}

/**
 * Der Schalter für die automatischen Review-Abgleiche.
 *
 * Ausgeblendet ist die Vorgabe. In cubic time kamen am 20. und 21.08.2026
 * dreiundvierzig Abgleiche zusammen — zweiundvierzig davon als Trigger, die
 * meisten weniger als eine Sekunde nach einer Merge-Request-Pipeline. Sie
 * drängten die Läufe aus dem Blick, um die es geht.
 *
 * Verschwiegen werden sie deshalb nicht: Die Zeile nennt ihre Zahl, damit
 * niemand eine Liste für vollständig hält, die es nicht ist. Eingeblendet
 * stehen sie nicht in einer eigenen Gruppe, sondern wieder an ihrer Stelle
 * im Zeitverlauf — sonst müsste man zweimal klicken, um einen Lauf zu sehen.
 */
function ReviewAbgleichSchalter({
  anzahl,
  sichtbar,
  umschalten,
}: {
  anzahl: number;
  sichtbar: boolean;
  umschalten: () => void;
}) {
  const wortlaut =
    anzahl === 1
      ? "1 automatischer Review-Abgleich"
      : `${anzahl} automatische Review-Abgleiche`;
  return (
    <div className="flex flex-wrap items-center gap-x-2 gap-y-1 px-1 text-xs text-gray-500 dark:text-gray-400">
      <span>
        {wortlaut} {sichtbar ? "eingeblendet" : "ausgeblendet"} · ausschließlich
        „{REVIEW_ABGLEICH_JOB}“
      </span>
      <button
        type="button"
        onClick={umschalten}
        aria-pressed={sichtbar}
        className="cursor-pointer rounded-md font-medium text-blue-600 underline underline-offset-2 hover:text-blue-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-500 dark:text-blue-400 dark:hover:text-blue-300"
      >
        {sichtbar ? "Ausblenden" : "Einblenden"}
      </button>
    </div>
  );
}

function Kennzahlen({ pipelines }: { pipelines: PipelineAnzeige[] }) {
  const gesamt = pipelines.length;
  const erfolgreich = pipelines.filter(
    (p) => p.zustand.trim().toLowerCase() === "success",
  ).length;
  const fehlgeschlagen = pipelines.filter(
    (p) => p.zustand.trim().toLowerCase() === "failed",
  ).length;
  /*
   * Die Quote rechnet über **abgeschlossene** Läufe, nicht über alle
   * angezeigten.
   *
   * Vorher stand im Nenner jede Zeile. Ein laufender Lauf zog die Quote damit
   * herunter, obwohl über ihn noch nichts feststand — und eine stehengebliebene
   * Zeile tat es dauerhaft: Fünf Läufe, vier gescheitert, einer seit
   * sechsundvierzig Stunden auf „läuft", ergaben 0 %, obwohl der fünfte in
   * Wahrheit erfolgreich war. Was kein Ergebnis hat, gehört in keine
   * Ergebnisquote.
   */
  const abgeschlossen = pipelines.filter((p) => !istLaufend(p)).length;
  const offen = gesamt - abgeschlossen;
  const dauern = pipelines
    .map((p) => p.dauerSekunden)
    .filter((d): d is number => d !== null);
  const durchschnitt = dauern.length
    ? dauern.reduce((a, b) => a + b, 0) / dauern.length
    : null;
  const quote =
    abgeschlossen > 0 ? Math.round((erfolgreich / abgeschlossen) * 100) : null;

  const karten: {
    label: string;
    wert: string;
    meldet?: boolean;
    leer?: boolean;
  }[] = [
    /*
     * „Angezeigt", nicht „Pipelines": Der Wert zählt die Läufe **dieser
     * Seite**, und die drei Kennzahlen daneben rechnen ebenfalls nur über sie.
     * „Pipelines 10" neben einem Band, das 87 nennt, behauptete zweierlei
     * zugleich — und zählte obendrein Läufe, nicht Pipelines.
     */
    { label: "Angezeigt", wert: String(gesamt), leer: gesamt === 0 },
    {
      label:
        offen > 0 ? `Erfolgsquote (${abgeschlossen} beendet)` : "Erfolgsquote",
      /*
       * Ein Strich, keine Null: Solange kein Lauf abgeschlossen ist, gibt es
       * keine Quote. „0 %" wäre eine Aussage, und zwar die falsche.
       */
      wert: quote === null ? "—" : `${quote} %`,
      leer: quote === null,
    },
    {
      label: "Ø Dauer",
      wert: formatiereDauer(
        durchschnitt === null ? null : Math.round(durchschnitt),
      ),
      leer: durchschnitt === null,
    },
    {
      label: "Fehlgeschlagen",
      wert: String(fehlgeschlagen),
      meldet: fehlgeschlagen > 0,
      leer: fehlgeschlagen === 0,
    },
  ];

  /*
    Ein ruhiges Raster statt innerer Trennlinien: Die gemeinsame Ausrichtung
    hält die Werte zusammen, während Abstand und Fläche jede Kennzahl lesbar
    machen. Das bleibt auch im zweispaltigen mobilen Raster eindeutig.
  */
  return (
    <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
      {karten.map((karte) => (
          <div
            key={karte.label}
            className="flex flex-col gap-0.5 rounded-xl bg-white px-4 py-3 shadow-sm dark:bg-gray-925"
          >
            <span className="truncate text-xs text-gray-500 dark:text-gray-400">
              {karte.label}
            </span>
            <span
              className={`text-xl font-semibold tabular-nums tracking-titel ${
                karte.meldet
                  ? "text-marke-warnung dark:text-orange-400"
                  : karte.leer
                    ? "text-gray-400 dark:text-gray-600"
                    : "text-gray-900 dark:text-gray-50"
              }`}
            >
              {karte.wert}
            </span>
          </div>
        ))}
    </div>
  );
}

export function PipelineAbschnitt({
  projektId,
  pipelines,
  isLoading,
  isError,
  erneut,
}: {
  projektId: string;
  pipelines: PipelineAnzeige[];
  isLoading: boolean;
  isError: boolean;
  erneut: () => void;
}) {
  /*
   * Solange ein Lauf offen ist, zählt die Anzeige mit. Ist keiner mehr offen,
   * steht der Takt still — eine fertige Liste braucht kein Neuzeichnen je
   * Sekunde.
   */
  const jetzt = useSekundentakt(pipelines.some(istLaufend));

  const [offenesLog, setzeOffenesLog] = useState<{
    pipelineId: string;
    jobId: number;
  } | null>(null);

  /*
   * Die automatischen Abgleiche sind bei jedem Aufruf zunächst aus. Wer sie
   * sehen will, blendet sie ein; die Entscheidung gilt für diese Ansicht und
   * überdauert sie nicht.
   */
  const [zeigeAbgleiche, setzeZeigeAbgleiche] = useState(false);

  const { uebrige, abgleiche } = teileReviewAbgleiche(pipelines);
  const sichtbarePipelines = zeigeAbgleiche ? pipelines : uebrige;

  if (isLoading) {
    return (
      <p className="rounded-lg bg-gray-50 px-4 py-6 text-center text-sm text-gray-400 dark:bg-gray-900 dark:text-gray-500">
        Lade Pipelines …
      </p>
    );
  }
  if (isError) {
    return (
      <div
        className="space-y-2 rounded-lg bg-red-50 px-4 py-3 dark:bg-red-950/40"
        role="alert"
      >
        <p className="text-sm text-red-700 dark:text-red-400">
          Pipelines konnten nicht geladen werden.
        </p>
        <Button variant="secondary" onClick={erneut}>
          Erneut versuchen
        </Button>
      </div>
    );
  }
  if (pipelines.length === 0) {
    return (
      <p className="rounded-lg bg-gray-50 px-4 py-6 text-center text-sm text-gray-500 dark:bg-gray-900 dark:text-gray-400">
        Noch keine Pipelines gespeichert.
      </p>
    );
  }

  /*
   * Leiste und Liste laufen in **derselben** Richtung: neuester Lauf links
   * beziehungsweise oben, ältere wandern nach rechts beziehungsweise nach
   * unten.
   *
   * Die Leiste war zwischenzeitlich umgekehrt, weil sie sich von links nach
   * rechts wie ein Zeitverlauf liest. Das ist eine Lesart, keine Vorgabe — und
   * sie zwingt den Blick, für dieselbe Sache zwei Richtungen zu halten. So
   * beginnt beides beim Jüngsten.
   *
   * Vorher teilten sich beide zusätzlich ein `toReversed()`, das die Liste mit
   * dem ältesten Lauf obenan zeigte. Das `slice(0, 30)` daneben lief ins Leere:
   * `PaginierterBereich` reicht ohnehin nur die aktuelle Seite herein.
   */
  return (
    <div className="space-y-4">
      {/*
        Leiste und Kennzahlen lassen die Abgleiche immer außen vor, auch
        wenn sie eingeblendet sind: Sie messen die Läufe, die etwas über den
        Stand des Projekts sagen. Zählten die Abgleiche mit, spränge die
        Erfolgsquote allein durch das Umschalten.
      */}
      {uebrige.length > 0 ? (
        <>
          <Tracker
            bloecke={uebrige.map((lauf) => pipelineTrackerBlock(lauf, jetzt))}
            beschreibung={`${
              uebrige.filter(
                (pipeline) =>
                  pipeline.zustand.trim().toLowerCase() === "failed",
              ).length
            } fehlgeschlagene von ${
              uebrige.length
            } Läufen ohne die automatischen Abgleiche.`}
          />
          <Kennzahlen pipelines={uebrige} />
        </>
      ) : null}
      {abgleiche.length > 0 ? (
        <ReviewAbgleichSchalter
          anzahl={abgleiche.length}
          sichtbar={zeigeAbgleiche}
          umschalten={() => setzeZeigeAbgleiche((vorher) => !vorher)}
        />
      ) : null}
      <div className="space-y-3">
        {sichtbarePipelines.map((pipeline) => (
          <PipelineKarte
            key={pipeline.id}
            projektId={projektId}
            pipeline={pipeline}
            offenesLog={offenesLog}
            setzeOffenesLog={setzeOffenesLog}
            jetzt={jetzt}
          />
        ))}
      </div>
      {pipelines.length > 30 ? (
        <p className="text-xs text-gray-500">
          Nur die letzten 30 Pipelines werden angezeigt.
        </p>
      ) : null}
    </div>
  );
}
